import type { Language } from 'src/enums/language';
import type { BuildType } from 'src/enums/build/build-type';
import { isClubLinkCurrentPage, isGlobalLinkCurrentPage } from 'src/helpers/navigation-helper';
import type { Styling } from 'src/context/styling';
import { PromoRibbonStyle, RecentlyViewedClubRibbonStyle } from 'src/enums/promo-ribbon-style';
import mq, { mqReducedMotion } from 'src/styling/media-queries';
import type { CSSInterpolation } from '@emotion/serialize';
import { colors } from 'src/styling/constants';
import { getLocalStorageItem, setLocalStorageItem } from 'src/helpers/local-storage-helper';

export const MOST_RECENTLY_VIEW_CLUB_ID = 'RecentlyViewedClubId';
export const MOST_RECENTLY_VIEW_SEARCH_CLUB_ID = 'RecentlyViewedSearchClubId';

export const isPromoEnabledOnPage = (
  promoRibbon: Queries.HeaderPromoRibbon | Queries.GlobalHeaderRecentlyViewedClubRibbon | undefined,
  basePath: string,
  language: Language,
  buildType: BuildType,
  clubId?: string
) => {
  if (!promoRibbon?.enabled || !promoRibbon?.selectedPages) {
    return false;
  }

  if (promoRibbon.selectedPages.allPages) {
    return true;
  }

  const pagesEnabledOn = [
    ...(promoRibbon.selectedPages.homePage ? ['/'] : []),
    ...(promoRibbon.selectedPages.levelOnePages ?? []),
    ...(promoRibbon.selectedPages.levelTwoPages ?? []),
    ...(promoRibbon.selectedPages.levelThreePages ?? []),
  ];

  if (clubId) {
    return pagesEnabledOn.some((link) => isClubLinkCurrentPage(link, language, clubId, basePath));
  }

  return pagesEnabledOn.some((link) =>
    isGlobalLinkCurrentPage(link, language, buildType, basePath)
  );
};

export const backgroundAndTextColorForRibbon = (styling: Styling, color?: string | null) => {
  switch (color) {
    case PromoRibbonStyle.PLUM:
      return { backgroundColor: styling.colors.camelot, color: styling.colors.cream };
    case PromoRibbonStyle.GREEN:
      return { backgroundColor: styling.colors.darkGreen, color: styling.colors.cream };
    case PromoRibbonStyle.BLACK:
      return { backgroundColor: '#000', color: styling.colors.cream };
    case PromoRibbonStyle.RED:
      return { backgroundColor: styling.colors.errorBoundary, color: styling.colors.cream };
    case PromoRibbonStyle.LIGHT_GREEN:
      return { backgroundColor: styling.colors.lightGreen, color: styling.colors.capeCod };
    default:
      return { backgroundColor: styling.colors.camelot, color: styling.colors.cream };
  }
};

export const backgroundColorForRecentClubRibbon = (styling: Styling, color?: string | null) => {
  switch (color) {
    case RecentlyViewedClubRibbonStyle.PLUM:
      return styling.colors.camelot;
    case RecentlyViewedClubRibbonStyle.GREEN:
      return styling.colors.darkGreen;
    case RecentlyViewedClubRibbonStyle.PALE_GREEN:
      return colors.sageBackground;
    default:
      return styling.colors.capeCod;
  }
};

export const createAnimatedStyles = (
  componentHeight: number,
  shouldHide: boolean,
  isMobileNavOpen: boolean = false
) =>
  ({
    openCloseWrapper: [
      mq({
        width: '100%',
        transition: [
          isMobileNavOpen || shouldHide ? 'height 0.4s ease, visibility 0.4s' : 'height 0.4s ease',
          shouldHide ? 'height 0.4s ease, visibility 0.4s' : 'height 0.4s ease',
        ],
        visibility: [
          isMobileNavOpen || shouldHide ? 'hidden' : 'visible',
          shouldHide ? 'hidden' : 'visible',
        ],
        overflow: 'hidden',
        height: [
          isMobileNavOpen || shouldHide ? 0 : componentHeight,
          shouldHide ? 0 : componentHeight,
        ],
      }),
      mqReducedMotion({
        transition: [null, 'none'],
      }),
    ],
  }) satisfies Record<string, CSSInterpolation>;

export const setRecentlyViewedClubIdForKey = (key: string, clubId?: string) => {
  if (!clubId) return;
  const recentlyViewedClubId = getLocalStorageItem(key);

  if (!recentlyViewedClubId || recentlyViewedClubId !== clubId) {
    setLocalStorageItem(key, clubId);
  }
};
